import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<{ error?: string }>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  loading: true,
  signIn: async () => ({ error: 'Supabase não conectado' }),
  signOut: async () => ({ error: 'Supabase não conectado' }),
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, 'signIn' | 'signOut'>>({
    user: null,
    session: null,
    loading: true,
  });

  const signIn = async (email: string, password: string) => {
    const sb = getSupabaseClient();
    if (!sb) return { error: 'Supabase não conectado' };
    const { error } = await sb.auth.signInWithPassword({ email, password });
    return { error: error?.message };
  };

  const signOut = async () => {
    const sb = getSupabaseClient();
    if (!sb) return { error: 'Supabase não conectado' };
    const { error } = await sb.auth.signOut();
    return { error: error?.message };
  };

  useEffect(() => {
    const sb = getSupabaseClient();
    if (!sb) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    sb.auth.getSession().then(({ data }) => {
      setState({
        user: data.session?.user ?? null,
        session: data.session,
        loading: false,
      });
    });

    const {
      data: { subscription },
    } = sb.auth.onAuthStateChange((_event, session) => {
      setState({
        user: session?.user ?? null,
        session,
        loading: false,
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export async function signInWithEmail(email: string, password: string) {
  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };
  const { data, error } = await sb.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error: error?.message };
}

export async function signOutUser() {
  const sb = getSupabaseClient();
  if (!sb) return { error: 'Supabase não conectado' };
  const { error } = await sb.auth.signOut();
  return { error: error?.message };
}

export function useAdminGuard(navigate: ReturnType<typeof useNavigate>) {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  return { user, loading };
}
